/* Compact enhancement; existing wish-list owns saves/removals and page sync. */
(() => {
  'use strict';
  if (customElements.get('ns-favorites-drawer')) return;
  class FavoritesDrawer extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.dialog = this.querySelector('dialog');
      this.list = this.querySelector('[data-favorites-list]');
      this.message = this.querySelector('[data-favorites-message]');
      this.strings = JSON.parse(this.querySelector('[data-favorites-strings]').textContent);
      this.root = (window.Shopify?.routes?.root || this.dataset.root || '/').replace(/\/?$/, '/');
      this.cache = new Map();
      this.generation = 0;
      this.abortEvents = new AbortController();
      const opts = { signal: this.abortEvents.signal };
      document.addEventListener('click', event => this.onDocumentClick(event), { ...opts, capture: true });
      document.addEventListener('update:wishlist-mail', () => this.queueSync(), opts);
      document.addEventListener('shopify:section:load', () => this.enhanceLinks(), opts);
      window.addEventListener('storage', event => {
        if (event.key === 'wishlistItem' || event.key === null) { this.syncNative(); this.queueSync(); }
      }, opts);
      this.querySelectorAll('[data-favorites-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), opts));
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('ns-favorites-open');
        const visible = node => {
          if (!node?.isConnected || node.closest('side-drawer:not(.active)')) return false;
          const rect = node.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.bottom > 0 && rect.left < innerWidth && rect.top < innerHeight;
        };
        const target = visible(this.opener) ? this.opener : [...document.querySelectorAll('a.header-wishlist, side-drawer-opener[data-side-drawer="#Drawer-Menu"] button')].find(visible);
        target?.focus({ preventScroll: true });
      }, opts);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('button:not(:disabled), a[href], [tabindex="0"]')].filter(node => node.getClientRects().length > 0);
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }, opts);
      this.dialog.addEventListener('click', event => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.dialog.close();
        const retry = event.target.closest('[data-favorites-retry]');
        if (retry) { this.cache.delete(retry.dataset.favoritesRetry); this.refresh(retry.dataset.favoritesRetry); }
      }, opts);
      this.enhanceLinks();
    }
    disconnectedCallback() {
      this.abortEvents?.abort();
      this.bound = false;
      this.generation++;
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('ns-favorites-open');
    }
    enhanceLinks() {
      document.querySelectorAll('a.header-wishlist').forEach(link => {
        if (!this.isWishlistLink(link)) return;
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-controls', this.dialog.id);
      });
    }
    isWishlistLink(link) {
      const url = new URL(link.href, location.href);
      return url.origin === location.origin && /\/pages\/(?:wishlist|wish-list)\/?$/.test(url.pathname);
    }
    onDocumentClick(event) {
      const link = event.target.closest('a.header-wishlist');
      if (link && this.isWishlistLink(link) && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && typeof this.dialog.showModal === 'function') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(link);
        return;
      }
      // Native wish-list stops bubbling. A queued task (not a microtask) waits
      // until every capture/target handler completes its localStorage writes.
      if (event.target.closest('[data-wishlist]')) {
        const remove = event.target.closest('[data-favorites-remove]');
        if (remove) this.pendingRemoveFocus = remove.dataset.favoritesRemove;
        this.queueSync();
      }
    }
    queueSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      setTimeout(() => {
        this.syncQueued = false;
        if (!this.isConnected) return;
        this.reconcileStandalone();
        if (this.dialog.open) this.refresh();
      }, 0);
    }
    readHandles() {
      const saved = JSON.parse(localStorage.getItem('wishlistItem') || '[]');
      if (!Array.isArray(saved) || saved.some(handle => typeof handle !== 'string' || !handle || /[\/\\?#\u0000-\u001f]/.test(handle))) throw new Error(this.strings.storage_error);
      return [...new Set(saved)];
    }
    syncNative() {
      try {
        const handles = this.readHandles();
        const native = document.querySelector('wish-list');
        native?.checkWishlistCount?.(handles);
        document.querySelectorAll('[data-wishlist-handle]').forEach(button => native?.setProductForWishlist?.(button.dataset.wishlistHandle));
      } catch (_) { /* Opening the drawer presents the actionable storage error. */ }
    }
    reconcileStandalone() {
      try {
        const handles = this.readHandles();
        let pruned = false;
        document.querySelectorAll('[data-wishlist-container] [data-wishlist-added][data-product-handle]').forEach(card => {
          // Legacy renderer puts an undefined ID on its outer card. Resolve
          // identity from its real heart control, never from a guessed product.
          const toggle = card.querySelector('[data-wishlist][data-product-id]');
          if (toggle?.dataset.productId) card.dataset.wishlistAdded = 'wishlist-' + toggle.dataset.productId;
          if (!handles.includes(card.dataset.productHandle)) { card.remove(); pruned = true; }
        });
        if (pruned) document.dispatchEvent(new CustomEvent('update:pagination', { bubbles: true }));
      } catch (_) { /* Leave existing page content intact if storage is denied. */ }
    }
    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener;
      document.querySelectorAll('ns-nav-gallery, ns-newbest').forEach(menu => menu.close?.());
      document.querySelectorAll('side-drawer[open], side-drawer.active, cart-drawer.active').forEach(drawer => drawer.close?.());
      this.dialog.showModal();
      document.documentElement.classList.add('ns-favorites-open');
      this.reconcileStandalone();
      this.refresh();
    }
    async json(path) {
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), 12000);
      try {
        const response = await fetch(this.root + path, { headers: { Accept: 'application/json' }, signal: abort.signal });
        if (!response.ok) { const error = new Error(this.strings.load_error); error.status = response.status; throw error; }
        return await response.json();
      } finally { clearTimeout(timer); }
    }
    product(handle) {
      const cached = this.cache.get(handle);
      if (cached && Date.now() - cached.time < 60000) return cached.promise;
      const promise = this.json('products/' + encodeURIComponent(handle) + '.js').then(product => {
        if (!product.id || !Array.isArray(product.variants)) throw new Error(this.strings.load_error);
        return product;
      }).catch(error => { this.cache.delete(handle); throw error; });
      this.cache.set(handle, { time: Date.now(), promise });
      return promise;
    }
    node(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text !== undefined) element.textContent = text;
      return element;
    }
    title(product) {
      return product.title;
    }

    price(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.currency, currencyDisplay: 'narrowSymbol' }).format(cents / 100);
    }
    productLink(product) { return this.root + 'products/' + encodeURIComponent(product.handle); }
    removeButton(handle, product) {
      const button = this.node('button', 'ns-favorites-remove', this.strings.remove);
      button.type = 'button';
      button.dataset.favoritesRemove = handle;
      button.setAttribute('aria-label', this.strings.remove_label.replace('{{ title }}', product ? this.title(product) : handle));
      if (product) {
        // Reuse native owner: standalone-page cards, counts and product hearts
        // are updated by exactly the same removal handler.
        const owner = this.node('wish-list');
        button.dataset.wishlist = '';
        button.dataset.wishlistHandle = handle;
        button.dataset.productId = String(product.id);
        button.classList.add('wishlist-added');
        owner.append(button);
        return owner;
      }
      button.addEventListener('click', () => {
        try {
          localStorage.setItem('wishlistItem', JSON.stringify(this.readHandles().filter(item => item !== handle)));
          this.syncNative();
          document.dispatchEvent(new CustomEvent('update:wishlist-mail', { bubbles: true }));
          this.refresh();
        } catch (_) { this.message.textContent = this.strings.storage_error; }
      });
      return button;
    }
    row(handle, product, error) {
      const row = this.node('li', 'ns-favorites-item');
      row.dataset.favoritesHandle = handle;
      if (!product) {
        const image = this.node('span', 'ns-favorites-image', this.strings.image_missing);
        const info = this.node('div', 'ns-favorites-info');
        info.append(this.node('p', 'ns-favorites-name', handle), this.node('p', 'ns-favorites-detail', error?.status === 404 ? this.strings.unavailable : this.strings.load_error));
        if (error?.status !== 404) {
          const retry = this.node('button', 'ns-favorites-action', this.strings.retry);
          retry.type = 'button'; retry.dataset.favoritesRetry = handle; info.append(retry);
        }
        info.append(this.removeButton(handle)); row.append(image, info); return row;
      }
      const image = this.node('a', 'ns-favorites-image');
      image.href = this.productLink(product);
      if (product.featured_image) {
        const img = this.node('img');
        const url = new URL(product.featured_image, location.href);
        if (url.protocol === 'https:' && (url.hostname.endsWith('.shopify.com') || url.origin === location.origin)) {
          url.searchParams.set('width', '328'); img.src = url.href;
          img.width = 164; img.height = 172; img.alt = this.title(product); img.decoding = 'async';
          img.addEventListener('error', () => { image.replaceChildren(document.createTextNode(this.strings.image_missing)); }, { once: true });
          image.append(img);
        } else image.textContent = this.strings.image_missing;
      } else image.textContent = this.strings.image_missing;
      const info = this.node('div', 'ns-favorites-info');
      const name = this.node('a', 'ns-favorites-name', this.title(product)); name.href = image.href;
      info.append(name);

      const available = product.variants.filter(variant => variant.available);
      const cents = Number(product.price_min ?? product.price);
      const price = this.node('p', 'ns-favorites-price', this.price(cents));
      if (product.price_varies && Number(product.price_max) > cents) price.textContent += ' – ' + this.price(Number(product.price_max));
      info.append(price);
      const variant = available[0];
      const rule = variant?.quantity_rule;
      const needsOptions = product.variants.length !== 1 || product.requires_selling_plan || (rule && (rule.min > 1 || rule.increment > 1));
      if (!available.length) {
        const button = this.node('button', 'ns-favorites-action', this.strings.sold_out); button.type = 'button'; button.disabled = true; info.append(button);
      } else if (needsOptions) {
        const link = this.node('a', 'ns-favorites-action', this.strings.options); link.href = image.href; info.append(link);
      } else {
        const form = this.node('form', 'ns-favorites-form'); form.method = 'post'; form.action = this.root + 'cart/add'; form.dataset.nsCartForm = '';
        const id = this.node('input'); id.type = 'hidden'; id.name = 'id'; id.value = String(variant.id);
        const quantity = this.node('input'); quantity.type = 'hidden'; quantity.name = 'quantity'; quantity.value = '1';
        const button = this.node('button', 'ns-favorites-action', this.strings.add); button.type = 'submit';
        const status = this.node('p'); status.dataset.nsFormStatus = ''; status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
        form.append(id, quantity, button, status); info.append(form);
      }
      info.append(this.removeButton(handle, product)); row.append(image, info); return row;
    }
    async refresh(retryHandle) {
      const version = ++this.generation;
      let handles;
      try { handles = this.readHandles(); }
      catch (_) { this.list.replaceChildren(); this.message.textContent = this.strings.storage_error; return; }
      this.querySelector('[data-favorites-count]').textContent = handles.length;
      const previousFocus = this.pendingRemoveFocus || (this.dialog.contains(document.activeElement) ? document.activeElement.closest('[data-favorites-handle]')?.dataset.favoritesHandle : null);
      const wasRemove = !!this.pendingRemoveFocus || !!document.activeElement?.matches('[data-favorites-remove]');
      this.pendingRemoveFocus = null;
      this.message.textContent = handles.length ? this.strings.loading : this.strings.empty;
      this.list.setAttribute('aria-busy', String(handles.length > 0));
      this.querySelector('[data-favorites-subtitle]').textContent = this.strings.subtitle;
      this.list.querySelectorAll('[data-favorites-handle]').forEach(row => { if (!handles.includes(row.dataset.favoritesHandle)) row.remove(); });
      if (!handles.length) { this.list.replaceChildren(); this.list.removeAttribute('aria-busy'); this.querySelector('[data-favorites-close]').focus({ preventScroll: true }); return; }
      if (!this.currency) {
        try { this.currency = (await this.json('cart.js')).currency || window.Shopify?.currency?.active; }
        catch (_) { this.currency = window.Shopify?.currency?.active; }
        if (!this.currency) { this.message.textContent = this.strings.load_error; this.list.removeAttribute('aria-busy'); return; }
      }
      const result = new Array(handles.length);
      let next = 0;
      await Promise.all(Array.from({ length: Math.min(4, handles.length) }, async () => {
        while (next < handles.length) {
          const index = next++;
          try { result[index] = { handle: handles[index], product: await this.product(handles[index]) }; }
          catch (error) { result[index] = { handle: handles[index], error }; }
        }
      }));
      if (version !== this.generation || !this.isConnected) return;
      const rows = result.map(item => this.row(item.handle, item.product, item.error));
      this.list.replaceChildren(...rows);
      this.list.querySelectorAll('[data-favorites-remove]').forEach(button => button.title = button.getAttribute('aria-label'));
      this.list.removeAttribute('aria-busy');
      this.message.textContent = '';

      if (wasRemove || retryHandle) {
        const targetHandle = retryHandle || (handles.includes(previousFocus) ? previousFocus : handles[0]);
        const target = rows.find(row => row.dataset.favoritesHandle === targetHandle)?.querySelector('[data-favorites-remove], [data-favorites-retry]');
        target?.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('ns-favorites-drawer', FavoritesDrawer);
})();

